import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isPlatformAdministrator } from '../utils/userRoleUtils';
import { AdminNavbar } from '../components/Shared/AdminNavbar';
import { AdminSidebar } from '../components/Shared/AdminSidebar';
import { AGENT_PLATFORM_BASE } from '../constants/agentPlatformRoutes';
import { ControlPanelPage } from '../pages/Admin/ControlPanelPage';
import { CustomersPage } from '../pages/Admin/CustomersPage';
import { CustomerEditPage } from '../pages/Admin/CustomerEditPage';
import { PlansPage } from '../pages/Admin/PlansPage';
import { PlanEditPage } from '../pages/Admin/PlanEditPage';
import { RolesPage } from '../pages/Admin/RolesPage';
import { RoleEditPage } from '../pages/Admin/RoleEditPage';
import { PermissionsPage } from '../pages/Admin/PermissionsPage';
import { SystemPage } from '../pages/Admin/SystemPage';
import { EmailTemplatesPage } from '../pages/Admin/EmailTemplatesPage';
import { SearchConfigPage } from '../pages/Admin/SearchConfigPage';
import { DocumentsConfigPage } from '../pages/Admin/DocumentsConfigPage';
import { DocumentTypesPage } from '../pages/Admin/Documents/DocumentTypesPage';
import { DocumentTypeFormPage } from '../pages/Admin/Documents/DocumentTypeFormPage';
import { DocumentRelationshipTypesPage } from '../pages/Admin/Documents/DocumentRelationshipTypesPage';
import { WorkflowsPage } from '../pages/Admin/WorkflowsPage';
import { CalendarPage } from '../pages/Admin/CalendarPage';
import { SlaPage } from '../pages/Admin/SlaPage';
import { WorkflowPage } from '../pages/Admin/WorkflowPage';
import { WorkflowAssociationRulesPage } from '../pages/Admin/WorkflowAssociationRulesPage';
import SlaDashboard from '../pages/Admin/SlaDashboard';
import HangfirePage from '../pages/Admin/HangfirePage';
import { JobMonitoringPage } from '../pages/Admin/JobMonitoringPage';
import { JobVisibilityConfigPage } from '../pages/Admin/JobVisibilityConfigPage';
import { EmailQueuePage } from '../pages/Admin/EmailQueuePage';
import { ProductModulesPage } from '../pages/Admin/Licensing/ProductModulesPage';
import { FeaturesPage } from '../pages/Admin/Licensing/FeaturesPage';
import { AdminUsersPage } from '../pages/Admin/AdminUsersPage';

const ChatbotWorkspaceEditRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const q = id ? `?id=${encodeURIComponent(id)}` : '';
  return <Navigate to={`/chatbot-admin/workspaces/edit${q}`} replace />;
};

const LegacyAgentPlatformRedirect: React.FC = () => {
  const location = useLocation();
  const target = location.pathname.replace('/admin/agent-platform', AGENT_PLATFORM_BASE);
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
};

const AdminLayout: React.FC = () => {
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  if (!isLoading && !isPlatformAdministrator(user)) {
    return <Navigate to="/unauthorized" replace />;
  }

  const isInvalidRoute = ![
    '/admin/control-panel',
    '/admin/customers',
    '/admin/users',
    '/admin/plans',
    '/admin/licensing',
    '/admin/roles',
    '/admin/permissions',
    '/admin/documents',
    '/admin/documents/types',
    '/admin/documents/relationship-types',
    '/admin/documents/workflows',
    '/admin/documents/calendar',
    '/admin/documents/sla',
    '/admin/workflow',
    '/admin/workflow/sla-dashboard',
    '/admin/workflow-association-rules',
    '/admin/system',
    '/admin/emails',
    '/admin/email-templates',
    '/admin/settings',
    '/admin/search',
    '/admin/hangfire',
    '/admin/jobs',
    '/admin/job-visibility',
    '/admin/email-queue',
    '/admin/agents',
  ].some(route => location.pathname.startsWith(route));

  if (location.pathname.startsWith('/admin/agent-platform')) {
    return <LegacyAgentPlatformRedirect />;
  }

  if (isInvalidRoute && location.pathname !== '/admin') {
    return <Navigate to="/admin/control-panel" replace />;
  }

  return (
    <div className="h-dvh overflow-hidden flex flex-col bg-gray-50">
      <AdminNavbar onOpenMobileNav={() => setMobileNavOpen(true)} />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <AdminSidebar
          mobileOpen={mobileNavOpen}
          onMobileClose={() => setMobileNavOpen(false)}
        />
        <main className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6">
          <Routes>
            <Route path="/" element={<Navigate to="/admin/control-panel" replace />} />
            <Route path="/control-panel" element={<ControlPanelPage />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/customers/:id/edit" element={<CustomerEditPage />} />
            <Route path="/users" element={<AdminUsersPage />} />
            <Route path="/plans" element={<PlansPage />} />
            <Route path="/plans/new" element={<PlanEditPage />} />
            <Route path="/plans/:id/edit" element={<PlanEditPage />} />
            <Route path="/licensing/product-modules" element={<ProductModulesPage />} />
            <Route path="/licensing/features" element={<FeaturesPage />} />
            <Route path="/roles" element={<RolesPage />} />
            <Route path="/roles/new" element={<RoleEditPage />} />
            <Route path="/roles/:id/edit" element={<RoleEditPage />} />
            <Route path="/permissions" element={<PermissionsPage />} />
            <Route path="/documents" element={<DocumentsConfigPage />} />
            <Route path="/documents/types" element={<DocumentTypesPage />} />
            <Route path="/documents/types/new" element={<DocumentTypeFormPage />} />
            <Route path="/documents/types/:id/edit" element={<DocumentTypeFormPage />} />
            <Route path="/documents/relationship-types" element={<DocumentRelationshipTypesPage />} />
            <Route path="/documents/workflows" element={<Navigate to="/admin/workflow/workflows" replace />} />
            <Route path="/documents/calendar" element={<Navigate to="/admin/workflow/calendar" replace />} />
            <Route path="/documents/sla" element={<Navigate to="/admin/workflow/sla" replace />} />
            <Route path="/workflow" element={<Navigate to="/admin/workflow/calendar" replace />} />
            <Route path="/workflow/calendar" element={<CalendarPage />} />
            <Route path="/workflow/sla" element={<SlaPage />} />
            <Route path="/workflow/workflows" element={<WorkflowPage />} />
            <Route path="/workflow/sla-dashboard" element={<SlaDashboard />} />
            <Route path="/workflow-association-rules" element={<WorkflowAssociationRulesPage />} />
            <Route path="/system" element={<SystemPage />} />
            <Route path="/emails" element={<EmailTemplatesPage />} />
            <Route path="/email-templates" element={<EmailTemplatesPage />} />
            <Route path="/search" element={<SearchConfigPage />} />
            <Route path="/hangfire" element={<HangfirePage />} />
            <Route path="/jobs" element={<JobMonitoringPage />} />
            <Route path="/job-visibility" element={<JobVisibilityConfigPage />} />
            <Route path="/email-queue" element={<EmailQueuePage />} />
            <Route path="/agents" element={<Navigate to={AGENT_PLATFORM_BASE} replace />} />
            <Route path="/agents/:id/edit" element={<ChatbotWorkspaceEditRedirect />} />
            <Route path="*" element={<Navigate to="/admin/control-panel" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
